import { formatCurrency, formatNumber, parseBigFloat } from "@west-tennessee-consulting/ui";
import type { Offerings } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderMetric, HeaderSubtitleSegment } from "../model";

/**
 * Offerings detail data is fetched via Supabase REST `.select("*")` (all real
 * columns), so the coverage guard checks these against the generated column
 * schema instead of a GraphQL Document.
 */
export const OFFERING_HEADER_FIELDS = [
  "pricing_model",
  "pricing_display",
  "base_price",
  "total_cost",
  "total_hours",
] as const satisfies readonly (keyof Offerings)[];

export type OfferingHeaderData = Pick<Offerings, (typeof OFFERING_HEADER_FIELDS)[number]>;

/**
 * 1:1 scalar columns edited in the header (dropped from the inline panel).
 * `total_cost` / `total_hours` are computed from line items, not directly
 * editable, so only `base_price` is here.
 */
export const OFFERING_HEADER_EDITABLE_FIELDS = [
  "base_price",
] as const satisfies readonly (keyof Offerings)[];
type OfferingHeaderEditableField = (typeof OFFERING_HEADER_EDITABLE_FIELDS)[number];

export function buildOfferingHeaderModel(offering: OfferingHeaderData): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  const pricingLabel = offering.pricing_display || offering.pricing_model;
  if (pricingLabel) subtitleSegments.push({ text: pricingLabel });

  const metrics: HeaderMetric[] = [];
  const basePrice = formatCurrency(offering.base_price);
  if (basePrice) {
    metrics.push({
      id: "base-price",
      label: "Base price",
      value: basePrice,
      fieldId: "base_price" satisfies OfferingHeaderEditableField,
    });
  }

  const totalCost = formatCurrency(offering.total_cost);
  if (totalCost) metrics.push({ id: "total-cost", label: "Total cost", value: totalCost });

  const priceNum = parseBigFloat(offering.base_price);
  const costNum = parseBigFloat(offering.total_cost);
  if (priceNum !== null && costNum !== null && priceNum > 0) {
    const marginPct = ((priceNum - costNum) / priceNum) * 100;
    metrics.push({ id: "margin", label: "Margin", value: `${marginPct.toFixed(1)}%` });
  }

  const hours = formatNumber(offering.total_hours);
  if (hours) metrics.push({ id: "hours", label: "Hours", value: `${hours}h` });

  return { subtitleSegments, facts: [], metrics };
}
